// src/pages/cashier/CashierSettlementPage.tsx
import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { settleCashierWallet } from "../../services/cashier-order.service";
import { useUserStore } from "../../store/user.store";
import { useAuth } from "../../context/AuthProvider";
import { WalletTransaction, Wallet } from "../../types";
import toast from "react-hot-toast";

interface Props {
  onBack: () => void;
}

const CashierSettlementPage: React.FC<Props> = ({ onBack }) => {
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [loading, setLoading] = useState(false);
  const [settling, setSettling] = useState(false);

  const { userProfile } = useUserStore();
  const { user } = useAuth();
  const profile = userProfile || user;

  useEffect(() => {
    if (profile) {
      loadData();
    }
  }, [profile]);

  const loadData = async () => {
    if (!profile) return;

    try {
      setLoading(true);

      // Get wallet
      const walletRef = doc(db, "wallets", profile.uid);
      const walletSnap = await getDoc(walletRef);
      if (walletSnap.exists()) {
        setWallet(walletSnap.data() as Wallet);
      }

      // Get "due" transactions
      const q = query(
        collection(db, "wallet_transactions"),
        where("walletId", "==", profile.uid),
        where("status", "==", "due")
      );
      const snap = await getDocs(q);
      const txs = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as WalletTransaction[];

      setTransactions(txs);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load settlement data");
    } finally {
      setLoading(false);
    }
  };

  const handleSettle = async () => {
    if (!profile) return;

    try {
      setSettling(true);

      const result = await settleCashierWallet(profile.uid);

      toast.success(
        `Settlement complete! ₹${(result.settledAmount ?? 0).toLocaleString()} settled from ${result.transactionCount} transaction(s)`
      );

      // Reload
      await loadData();
    } catch (error) {
      console.error("Settlement error:", error);
      toast.error("Settlement failed");
    } finally {
      setSettling(false);
    }
  };

  if (!profile) {
    return <div className="text-center p-8 text-white">Loading settlement...</div>;
  }

  const totalDue = transactions.reduce((sum, tx) => sum + tx.amount, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white">
            💳 End-of-Day Settlement
          </h1>
          <button onClick={onBack} className="text-slate-300 hover:text-white">
            ← Back
          </button>
        </div>

        {/* Wallet Summary */}
        {wallet && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-blue-600 rounded-lg p-6 text-white">
              <p className="text-sm text-blue-100 mb-1">Settled Balance</p>
              <p className="text-3xl font-bold">
                ₹{(wallet.balance ?? 0).toLocaleString()}
              </p>
            </div>

            <div className="bg-orange-600 rounded-lg p-6 text-white">
              <p className="text-sm text-orange-100 mb-1">Due for Settlement</p>
              <p className="text-3xl font-bold">
                ₹{(wallet.dueBalance ?? 0).toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {/* Transactions */}
        {loading ? (
          <div className="text-center text-white">Loading transactions...</div>
        ) : transactions.length === 0 ? (
          <div className="bg-slate-700 rounded-lg p-8 text-center">
            <p className="text-slate-300 text-lg">✅ No pending transactions</p>
            <p className="text-slate-400 text-sm mt-2">All settled!</p>
          </div>
        ) : (
          <div className="bg-slate-700 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-white mb-4">
              📋 Pending Transactions ({transactions.length})
            </h2>

            <div className="space-y-3">
              {transactions.map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between p-4 bg-slate-600 rounded-lg"
                >
                  <div>
                    <p className="font-semibold text-white">
                      Order #{tx.orderId?.substring(0, 6)}
                    </p>
                    <p className="text-sm text-slate-300">
                      {tx.mode.toUpperCase()} •{" "}
                      {new Date(tx.createdAt?.toDate?.()).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-lg font-bold text-green-400">
                    ₹{tx.amount.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-600">
              <div className="flex justify-between items-center">
                <p className="text-lg font-bold text-white">
                  Total to Settle
                </p>
                <p className="text-2xl font-bold text-green-400">
                  ₹{totalDue.toLocaleString()}
                </p>
              </div>
            </div>

            <button
              onClick={handleSettle}
              disabled={settling || totalDue === 0}
              className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-lg transition-colors disabled:opacity-50"
            >
              {settling ? "Processing..." : `Settle ₹${totalDue.toLocaleString()}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashierSettlementPage;
